import { useMemo, useState } from 'react';
import { useCreateTenantUserMutation } from '../mutations/useTenantUserMutations';
import { useTenantProfileQuery } from '../queries/useTenantProfileQuery';
import { useTenantUsersQuery } from '../queries/useTenantUsersQuery';
import { useI18n } from '../../../i18n/useI18n';
import { toastMutationError } from '../../../query/mutationToasts';
import { toast } from '../../../toast/toast';
import type {
  CreateDirectoryUserInput,
  DirectoryUser,
} from '../../../types/user';
import type { TenantUsersStatusFilter } from '../tenantUsersListParams';
import { useTenantUsersSearchParams } from './useTenantUsersSearchParams';

function formatUserDisplayName(
  user: DirectoryUser,
  unknownUserLabel: string,
): string {
  const fullName = [user.firstName?.trim(), user.lastName?.trim()]
    .filter((part): part is string => Boolean(part))
    .join(' ');

  if (fullName !== '') {
    return fullName;
  }

  const username = user.username?.trim();
  if (username) {
    return username;
  }

  return unknownUserLabel;
}

function matchesStatusFilter(
  user: DirectoryUser & { enabled?: boolean | null },
  statusFilter: TenantUsersStatusFilter,
): boolean {
  if (statusFilter === 'all') {
    return true;
  }
  if (user.enabled == null) {
    return false;
  }
  return statusFilter === 'enabled' ? user.enabled : !user.enabled;
}

export function useTenantUsersTab() {
  const { translate } = useI18n();
  const usersQuery = useTenantUsersQuery();
  const profileQuery = useTenantProfileQuery();
  const createUserMutation = useCreateTenantUserMutation();
  const filters = useTenantUsersSearchParams();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const filteredUsers = useMemo(() => {
    const users = usersQuery.data ?? [];
    const query = filters.debouncedSearch.trim().toLowerCase();

    return users.filter((user) => {
      if (!matchesStatusFilter(user, filters.statusFilter)) {
        return false;
      }
      if (query === '') {
        return true;
      }

      const personName = formatUserDisplayName(user, '').toLowerCase();
      return (
        personName.includes(query) ||
        (user.username?.toLowerCase().includes(query) ?? false) ||
        (user.email?.toLowerCase().includes(query) ?? false) ||
        (user.firstName?.toLowerCase().includes(query) ?? false) ||
        (user.lastName?.toLowerCase().includes(query) ?? false)
      );
    });
  }, [usersQuery.data, filters.debouncedSearch, filters.statusFilter]);

  const handleCreateUser = (input: CreateDirectoryUserInput) => {
    createUserMutation.mutate(input, {
      onSuccess: (data) => {
        if (data.roleAssigned) {
          toast.success({
            title: translate(
              'organisationManagement.users.create.successTitle',
            ),
            description: translate(
              'organisationManagement.users.create.successDescription',
              { username: input.username },
            ),
          });
        }
        setIsCreateDialogOpen(false);
        if (!data.roleAssigned) {
          toast.error({
            title: translate(
              'organisationManagement.users.create.roleErrorTitle',
            ),
            description: translate(
              'organisationManagement.users.create.roleErrorDescription',
              { username: input.username },
            ),
          });
        }
      },
      onError: (error) => {
        void toastMutationError(
          error,
          translate('organisationManagement.users.create.errorTitle'),
          translate('organisationManagement.users.create.errorDescription'),
        );
      },
    });
  };

  const users = usersQuery.data ?? [];
  const userCount = users.length;
  const maxUsers = profileQuery.data?.maxUsers;
  const isTenantFull = maxUsers != null && userCount >= maxUsers;
  const hasUsers = userCount > 0;

  return {
    translate,
    usersQuery,
    createUserMutation,
    filters,
    filteredUsers,
    isCreateDialogOpen,
    setIsCreateDialogOpen,
    handleCreateUser,
    userCount,
    maxUsers,
    isTenantFull,
    hasUsers,
  };
}
